import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { AuthLayout, TextInput, Button, Alert } from '../components';
import { trackEvent } from '../services/analytics';

export const LoginPage: React.FC = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const sessionReason = searchParams.get('reason');
  const sessionMessage = sessionReason === 'expired'
    ? t('login.sessionExpired', { ns: 'auth' })
    : sessionReason === 'unauthorized'
      ? t('login.sessionUnauthorized', { ns: 'auth' })
      : '';

  React.useEffect(() => {
    if (user) {
      navigate('/');
    }
  }, [user, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      if (sessionReason) {
        setSearchParams({}, { replace: true });
      }
      await login(email, password);
      trackEvent('login');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('auth.loginFailed', { ns: 'validation' }));
      setIsLoading(false);
    }
  };

  return (
    <AuthLayout
      title={t('login.title', { ns: 'auth' })}
      subtitle={t('login.subtitle', { ns: 'auth' })}
    >
      <form className="space-y-6" onSubmit={handleSubmit} data-testid="login-form">
        {sessionMessage && (
          <Alert
            variant="info"
            message={sessionMessage}
            dismissible
            onDismiss={() => setSearchParams({}, { replace: true })}
          />
        )}
        {error && <Alert variant="error" message={error} dismissible onDismiss={() => setError('')} />}

        <TextInput
          id="email"
          label={t('login.emailOrPhoneLabel', { ns: 'auth' })}
          type="text"
          placeholder={t('login.emailOrPhonePlaceholder', { ns: 'auth' })}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          autoComplete="email"
        />

        <div>
          <div className="mb-2 flex items-baseline justify-between">
            <label htmlFor="password" className="block text-base font-semibold text-[#2b2b2b] dark:text-[#fffdf8]">
              {t('login.passwordLabel', { ns: 'auth' })}
            </label>
            <Link
              to="/forgot-password"
              className="text-sm font-semibold text-[#b64f42] dark:text-[#d4825a] hover:underline"
            >
              {t('login.forgotPassword', { ns: 'auth' })}
            </Link>
          </div>
          <TextInput
            id="password"
            type="password"
            placeholder={t('login.passwordPlaceholder', { ns: 'auth' })}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            autoComplete="current-password"
          />
        </div>

        <Button type="submit" variant="primary" size="md" fullWidth isLoading={isLoading}>
          {isLoading ? t('login.submitLoading', { ns: 'auth' }) : t('login.submitIdle', { ns: 'auth' })}
        </Button>

        <div className="space-y-3 pt-4 text-center">
          <p className="text-base font-semibold text-[#b64f42] dark:text-[#d4825a]">
            {t('login.newHere', { ns: 'auth' })}{' '}
            <Link to="/register" className="hover:underline">
              {t('login.createAccount', { ns: 'auth' })}
            </Link>
          </p>
          <Link
            to="/register"
            className="inline-block rounded-full border-2 border-[#2b2b2b] px-6 py-2.5 font-bold text-[#2b2b2b] transition-colors hover:bg-[#f5f1e8] dark:border-[#6b6560] dark:text-[#fffdf8] dark:hover:bg-[#3a3630]"
          >
            {t('buttons.register', { ns: 'common' })}
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
};
